/**
 * WebRTC Service
 * Peer connection management using Firestore signaling
 * Handles ICE candidate exchange via subcollections
 */

import { db } from '@/lib/firebase';
import {
  doc,
  collection,
  setDoc,
  onSnapshot,
  deleteDoc,
  serverTimestamp,
} from 'firebase/firestore';

// ICE servers — STUN for NAT discovery + TURN relays so calls work
// behind symmetric NAT and mobile networks (where STUN-only often fails).
// Using the Open Relay Project's free TURN service for now.
// ⚠️  For production: replace with Twilio TURN credentials or a self-hosted coturn instance.
const ICE_SERVERS: RTCIceServer[] = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
  {
    urls: [
      'turn:openrelay.metered.ca:80',
      'turn:openrelay.metered.ca:443',
      'turn:openrelay.metered.ca:443?transport=tcp',
      'turns:openrelay.metered.ca:443',
    ],
    username: 'openrelayproject',
    credential: 'openrelayproject',
  },
];

export interface PeerConnectionResult {
  pc: RTCPeerConnection;
  remoteStream: MediaStream;
}

/**
 * Create and configure an RTCPeerConnection
 * - If initiator, creates offer and saves to Firestore
 * - If not initiator, waits for offer, creates answer
 * - Both sides exchange ICE candidates via subcollections
 */
export async function createPeerConnection(
  callId: string,
  isInitiator: boolean,
  localStream: MediaStream,
): Promise<PeerConnectionResult> {
  const pcConfig: RTCConfiguration = {
    iceServers: ICE_SERVERS,
    iceTransportPolicy: 'all', // try direct first, relay via TURN if needed
  };

  const pc = new RTCPeerConnection(pcConfig);
  const remoteStream = new MediaStream();

  // Add local tracks to peer connection
  for (const track of localStream.getTracks()) {
    pc.addTrack(track, localStream);
  }

  // Collect remote tracks.
  // Use event.track directly — more reliable than event.streams which can be
  // empty on some browsers (Firefox, older Safari). Dedup by track.id.
  pc.ontrack = (event: RTCTrackEvent) => {
    const track = event.track;
    if (track && !remoteStream.getTracks().find(t => t.id === track.id)) {
      remoteStream.addTrack(track);
    }
    // Also add from streams if present (belt-and-suspenders)
    event.streams.forEach((stream) => {
      stream.getTracks().forEach((t) => {
        if (!remoteStream.getTracks().find(rt => rt.id === t.id)) {
          remoteStream.addTrack(t);
        }
      });
    });
  };

  // Handle ICE candidates from remote peer
  const receiverCandidatesPath = isInitiator
    ? `calls/${callId}/receiverCandidates`
    : `calls/${callId}/callerCandidates`;

  const iceCandidatesUnsub = onSnapshot(
    collection(db, receiverCandidatesPath),
    (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type === 'added') {
          const candidate = new RTCIceCandidate(change.doc.data() as RTCIceCandidateInit);
          pc.addIceCandidate(candidate).catch(() => {});
        }
      });
    },
  );

  // Send local ICE candidates to remote peer
  pc.onicecandidate = async (event) => {
    if (!event.candidate) return;

    const senderCandidatesPath = isInitiator
      ? `calls/${callId}/callerCandidates`
      : `calls/${callId}/receiverCandidates`;

    const candidateRef = doc(
      collection(db, senderCandidatesPath),
    );

    await setDoc(candidateRef, {
      ...event.candidate.toJSON(),
      timestamp: serverTimestamp(),
    }).catch(() => {});
  };

  // Offer/Answer exchange
  const callDocRef = doc(db, 'calls', callId);

  if (isInitiator) {
    // Create offer
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);

    // Save offer to Firestore
    await setDoc(
      callDocRef,
      {
        offer: {
          type: offer.type,
          sdp: offer.sdp,
        },
      },
      { merge: true },
    );

    // Wait for answer
    const answerUnsub = onSnapshot(callDocRef, async (snapshot) => {
      if (snapshot.data()?.answer?.sdp) {
        const answer = new RTCSessionDescription(snapshot.data()?.answer);
        try {
          await pc.setRemoteDescription(answer);
        } catch (err) {
          console.error('Failed to set remote description:', err);
        }
        answerUnsub();
      }
    });
  } else {
    // Wait for offer
    const offerUnsub = onSnapshot(callDocRef, async (snapshot) => {
      if (snapshot.data()?.offer?.sdp && pc.remoteDescription === null) {
        const offer = new RTCSessionDescription(snapshot.data()?.offer);
        try {
          await pc.setRemoteDescription(offer);

          // Create answer
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);

          // Save answer to Firestore
          await setDoc(
            callDocRef,
            {
              answer: {
                type: answer.type,
                sdp: answer.sdp,
              },
            },
            { merge: true },
          );
        } catch (err) {
          console.error('Failed to set remote or create answer:', err);
        }
        offerUnsub();
      }
    });
  }

  // Store cleanup function on pc for later
  (pc as any).__cleanupUnsub = () => {
    iceCandidatesUnsub();
  };

  return { pc, remoteStream };
}

/**
 * Close peer connection and clean up resources
 */
export function closePeerConnection(pc: RTCPeerConnection): void {
  // Call stored cleanup
  const cleanup = (pc as any).__cleanupUnsub;
  if (cleanup) cleanup();

  // Close all senders/receivers
  pc.getSenders().forEach((sender) => {
    try {
      pc.removeTrack(sender);
    } catch (e) {
      // ignore
    }
  });

  // Close connection
  if (pc.connectionState !== 'closed') {
    pc.close();
  }
}

/**
 * Get user media stream with specified constraints
 */
export async function getMediaStream(constraints: {
  audio?: boolean | MediaTrackConstraints;
  video?: boolean | MediaTrackConstraints;
}): Promise<MediaStream> {
  try {
    return await navigator.mediaDevices.getUserMedia(constraints);
  } catch (err) {
    console.error('Error getting media stream:', err);
    throw err;
  }
}

/**
 * Enumerate available media devices
 */
export async function enumerateDevices(): Promise<MediaDeviceInfo[]> {
  try {
    return await navigator.mediaDevices.enumerateDevices();
  } catch (err) {
    console.error('Error enumerating devices:', err);
    return [];
  }
}

/**
 * Stop all tracks in a stream
 */
export function stopMediaStream(stream: MediaStream): void {
  stream.getTracks().forEach((track) => {
    track.stop();
  });
}
